import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { LayoutGrid, Settings2, Beef, FileText, LogOut, Coins, ChevronDown, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import logoFull from "@/assets/logo-full.png";

export const Layout = ({ children }) => {
  const loc = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const nav = [
    { to: "/app", label: "Athletes", icon: LayoutGrid, match: (p) => p === "/app" || p.startsWith("/athlete") },
    { to: "/stock", label: "Stock", icon: Beef, match: (p) => p.startsWith("/stock") || p.startsWith("/animal") || p.startsWith("/contractor") },
    { to: "/presenter", label: "Presenter", icon: FileText, match: (p) => p.startsWith("/presenter") },
    ...(isAdmin ? [{ to: "/admin", label: "Admin", icon: Settings2, match: (p) => p.startsWith("/admin") }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#090A0C] rp-noise">
      <header
        data-testid="app-header"
        className="sticky top-0 z-50 border-b border-[#262933] bg-[#0B0D11]/90 backdrop-blur-xl"
      >
        <div className="max-w-[1700px] mx-auto px-5 md:px-8 h-28 flex items-center justify-between">
          <Link to="/app" data-testid="brand-logo" className="flex items-center group">
            <img
              src={logoFull}
              alt="Rodeo Intel"
              className="h-24 w-auto transition-transform duration-200 group-hover:scale-[1.03]"
            />
          </Link>

          <div className="flex items-center gap-2 md:gap-4">
            <nav className="flex items-center gap-1">
              {nav.map((n) => {
                const active = n.match(loc.pathname);
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    data-testid={`nav-${n.label.toLowerCase()}`}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200",
                      active
                        ? "bg-[#1A1D26] text-[#F3EFE6] border border-[#E8833A]/40"
                        : "text-[#9A9CA6] hover:text-[#F3EFE6] hover:bg-[#14171E] border border-transparent"
                    )}
                  >
                    <n.icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{n.label}</span>
                  </Link>
                );
              })}
            </nav>
            <UserMenu user={user} />
          </div>
        </div>
      </header>
      <main>{children}</main>
    </div>
  );
};

const UserMenu = ({ user }) => {
  const { logout, buyCredits } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user) return null;
  const isAdmin = user.role === "admin";
  const isComped = !!user.comped;
  const owed = user.credits < 0 ? -user.credits : 0;

  const doLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="relative" ref={ref}>
      {!isAdmin && isComped && (
        <span
          data-testid="comped-status"
          title="Comped account — usage is billed by the organizer"
          className="hidden md:inline-flex items-center gap-1.5 mr-2 px-3 py-1.5 rounded-lg bg-[#1A202C] border border-[#334155] text-sm font-semibold text-[#94A3B8]"
        >
          <ShieldCheck className="h-4 w-4" /> Comped{owed ? ` · ${owed} used` : ""}
        </span>
      )}
      {!isAdmin && !isComped && (
        <button
          data-testid="credit-balance"
          onClick={() => buyCredits()}
          title="Buy 25 credits for $25"
          className="hidden md:inline-flex items-center gap-1.5 mr-2 px-3 py-1.5 rounded-lg bg-[#1A1D26] border border-[#E8833A]/30 text-sm font-semibold text-[#E8833A] hover:bg-[#22262F] hover:border-[#E8833A]/60 transition-colors"
        >
          <Coins className="h-4 w-4" /> {user.credits} credits
        </button>
      )}
      <button
        data-testid="user-menu-button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1A1D26] border border-[#262933] text-sm text-[#F3EFE6] hover:border-[#E8833A]/50 transition-colors"
      >
        <span className="h-6 w-6 rounded-full bg-[#E8833A] text-[#0B0D11] text-xs font-bold flex items-center justify-center uppercase">
          {(user.name || user.email)[0]}
        </span>
        <span className="hidden sm:inline max-w-[120px] truncate">{user.name || user.email}</span>
        <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div data-testid="user-menu" className="absolute right-0 mt-2 w-60 rounded-xl border border-[#262933] bg-[#12141A] shadow-2xl shadow-black/50 p-2 z-50">
          <div className="px-3 py-2 border-b border-[#1E212A] mb-1">
            <div className="text-sm font-semibold text-[#F3EFE6] truncate">{user.name || "—"}</div>
            <div className="text-xs text-[#686B77] truncate">{user.email}</div>
            <div className="mt-2 flex items-center gap-2 text-xs">
              {isAdmin ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#122A1E] text-[#4ADE80] border border-[#1B4D36] font-mono uppercase tracking-wider">
                  <ShieldCheck className="h-3 w-3" /> Owner
                </span>
              ) : isComped ? (
                <span className="inline-flex items-center gap-1.5 text-[#94A3B8]">
                  <ShieldCheck className="h-3.5 w-3.5" /> Comped{owed ? ` · ${owed} credits used` : ""}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[#E8833A] font-semibold">
                  <Coins className="h-3.5 w-3.5" /> {user.credits} credits
                </span>
              )}
            </div>
          </div>
          {!isAdmin && isComped && (
            <div data-testid="comped-note" className="px-3 py-2 text-xs text-[#9A9CA6] leading-relaxed">
              Your scans are billed directly by the organizer — no card needed.
            </div>
          )}
          {!isAdmin && !isComped && (
            <button
              data-testid="buy-credits-button"
              onClick={() => buyCredits()}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#E8833A] hover:bg-[#1A1D26] transition-colors"
            >
              <Coins className="h-4 w-4" /> Buy 25 credits — $25
            </button>
          )}
          <button
            data-testid="logout-button"
            onClick={doLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#F3EFE6] hover:bg-[#1A1D26] transition-colors"
          >
            <LogOut className="h-4 w-4 text-[#FB923C]" /> Log out
          </button>
        </div>
      )}
    </div>
  );
};
